export const CLASSES = ['HUMAN', 'LIKELY_HUMAN', 'UNKNOWN', 'LIKELY_BOT', 'BOT', 'VERIFIED_BOT'] as const;
export type TrafficClass = typeof CLASSES[number];
export const FILTERS = ['human', 'people', 'uncertain', 'all', 'bots', 'search', 'ai'] as const;
export type TrafficFilter = typeof FILTERS[number];
export type BotCategory = '' | 'SEARCH_CRAWLER' | 'AI_CRAWLER' | 'SEO_CRAWLER' | 'MONITORING' | 'AUTOMATION';
export const CLASSIFIER_VERSION = '2026-09-22.1';
export const METRIC_VERSION = '1';
export const DAY = 86_400_000;
export const SESSION_IDLE = 30 * 60_000;
export const SESSION_MAX = DAY;
export const TRAFFIC_LABELS: Record<TrafficFilter, string> = {
  human: 'Human', people: 'Human + likely human', uncertain: 'Human + uncertain',
  all: 'All browser traffic', bots: 'Bots only', search: 'Search crawlers', ai: 'AI crawlers',
};

export interface Rules {
  version: number;
  hosting_asns: number[];
  blocked_asns: number[];
  weights: { webdriver: number; mismatch: number; hosting: number; velocity: number; periodic: number };
}
export const DEFAULT_RULES: Rules = {
  version: 1, hosting_asns: [], blocked_asns: [],
  weights: { webdriver: -35, mismatch: -25, hosting: -10, velocity: -30, periodic: -10 },
};
export interface Site {
  account_id?: string | null;
  public_token?: string | null; public_title?: string;
  id: string; name: string; origins: string[]; enabled: boolean;
  property_keys: string[]; goals: string[]; rules: Rules; created_at: number;
}
export interface Dimension { name: string; count: number }
export interface DailyPoint { date: string; pageviews: number; visitors: number; sessions: number; conversions: number }
export interface Report {
  daily_countries?: { date: string; counts: Record<string, number> }[];
  site_id: string; start: string; end: string; filter: TrafficFilter;
  totals: { pageviews: number; visitors: number; sessions: number; bounces: number; conversions: number; events: number; edge_requests: number; duration_ms: number; duration_samples: number };
  series: DailyPoint[];
  quality: Record<TrafficClass, number>;
  dimensions: { pages: Dimension[]; referrers: Dimension[]; countries: Dimension[]; devices: Dimension[]; campaigns: Dimension[]; events: Dimension[]; crawlers: Dimension[] };
  meta: { source: 'local' | 'r2-sql' | 'rollup'; computed_at: number; data_through: string; provisional: boolean; sampled: false; classifier_version: string; rules_version: number; metric_version: string; identity_scope: 'site-day'; visitor_label: 'Daily visitors' | 'Visitor-days'; timezone: 'UTC'; generation: string; dimensions_complete: boolean };
}
export interface Job {
  id: string; site_id: string; start: string; end: string; filter: TrafficFilter;
  status: 'queued' | 'running' | 'complete' | 'failed'; created_at: number; updated_at: number;
  error: string | null; result_key: string | null;
}
export interface JobMessage { id: string }
export function dayString(time: number): string { return new Date(time).toISOString().slice(0, 10); }
export function daysBetween(start: string, end: string): number { return (Date.parse(end) - Date.parse(start)) / DAY; }
export function emptyQuality(): Record<TrafficClass, number> {
  return { HUMAN: 0, LIKELY_HUMAN: 0, UNKNOWN: 0, LIKELY_BOT: 0, BOT: 0, VERIFIED_BOT: 0 };
}
export function included(classification: TrafficClass, category: string, filter: TrafficFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'human') return classification === 'HUMAN';
  if (filter === 'people') return classification === 'HUMAN' || classification === 'LIKELY_HUMAN';
  if (filter === 'uncertain') return ['HUMAN', 'LIKELY_HUMAN', 'UNKNOWN'].includes(classification);
  if (filter === 'search') return category === 'SEARCH_CRAWLER';
  if (filter === 'ai') return category === 'AI_CRAWLER';
  return ['BOT', 'LIKELY_BOT', 'VERIFIED_BOT'].includes(classification);
}
// Provisional human pageviews per website: 14 complete UTC days, current = last 7, previous = the 7 before.
export type Portfolio = { start: string; end: string; days: 7; provisional: true; sampled: boolean; sites: { id: string; series: number[]; current: number; previous: number }[] };
